import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";

import CategoryCard from "../components/CategoryCard";
import FeaturedCarousel from "../components/FeaturedCarousel";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import Breadcrumbs from "../components/Breadcrumbs";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";


function Shop({ products }) {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [flowerProducts, setFlowerProducts] = useState([]);
  const [accessoriesProducts, setAccessoriesProducts] = useState([]);
  const [vipProducts, setVipProducts] = useState([]);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const {
    selectedAccessories,
    setSelectedAccessories,
    selectedVips,
    setSelectedVips,
    selectedFlowers,
    setSelectedFlowers,
  } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/products`);
        const data = res.data.map((product) => ({
          ...product,
        }));

        setFeaturedProducts(data.filter((p) => p.featured));
        setFlowerProducts(data.filter((p) => p.category === "flower"));
        setAccessoriesProducts(data.filter((p) => p.category === "accessory"));
        setVipProducts(data.filter((p) => p.category === "vip"));
      } catch (error) {
        console.error("Failed to fetch products:", error);
      }
    };

    fetchProducts();
  }, [BASE_URL]);


  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const handleAddClick = (product) => {
    if (product.category === "flower") {
      // Toggle flower: allow multiple selections like accessories & VIPs
      const alreadySelected = selectedFlowers.some(
        (item) => item._id === product._id
      );

      if (alreadySelected) {
        setSelectedFlowers((prev) =>
          prev.filter((item) => item._id !== product._id)
        );
      } else {
        setSelectedFlowers((prev) => [...prev, product]);
      }
    }

    if (product.category === "accessory") {
      // Toggle accessory
      const alreadySelected = selectedAccessories.some(
        (item) => item._id === product._id
      );

      if (alreadySelected) {
        setSelectedAccessories((prev) =>
          prev.filter((item) => item._id !== product._id)
        );
      } else {
        setSelectedAccessories((prev) => [...prev, product]);
      }
    }

    if (product.category === "vip") {
      // Toggle VIP accessory
      const alreadySelected = selectedVips.some(
        (item) => item._id === product._id
      );

      if (alreadySelected) {
        setSelectedVips((prev) =>
          prev.filter((item) => item._id !== product._id)
        );
      } else {
        setSelectedVips((prev) => [...prev, product]);
      }
    }
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleSeeAllAccessories = () => {
    navigate("/accessories", {
      state: { selectedAccessories },
    });
  };

  const handleSeeAllVips = () => {
    navigate("/vip", {
      state: { selectedVips },
    });
  };

  return (
    <>
      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto px-4">
        <Breadcrumbs />
      </div>

      {/* Hero Section */}
      <section className="bg-gray-100 py-20 text-center px-4 md:px-6" data-aos="fade-down">
        <h1 className="text-4xl font-bold text-center mb-4">Shop Hampers</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-10">
          Discover our curated range of flower hamper gifts. Choose from elegant single-accessory bundles,
          double-accessory delights, or VIP designer selections — all available for free same-day delivery.
          Thoughtful gifting made effortless.
        </p>
      </section>

      {/* Category Cards */}
      <section className="px-4 md:px-6 py-16 max-w-7xl mx-auto" data-aos="fade-up">
        <div className="col-span-full" id="shop-main-content">
          <h2 className="text-3xl font-bold text-center mb-4">Explore Gift Categories</h2>
          <p className="text-center text-gray-500 max-w-2xl mx-auto mb-10">
            Choose a hamper type to start building a custom gift. Pair elegant accessories with fresh flowers,
            or shop exclusive VIP items. Click "Start Building" to begin.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <CategoryCard
              title="Blossom Delight - Flower Gift + 1 Accessory (3% OFF)"
              image="/images/category-card/accessory.jpg"
              description="Perfect for simple, stylish surprises."
              hamperType="BlossomDelight"
            />
            <CategoryCard
              title="Charming Bloom - Flower Gift + 2 Accessories (4% OFF)"
              image="/images/category-card/accessories.jpg"
              description="Two accessories, double the delight."
              hamperType="CharmingBloom"
            />
            <CategoryCard
              title="VIP Opulence - Flower Gift + VIP Collections (5% OFF)"
              image="/images/category-card/vip.jpg"
              description="Premium designer gifts for special moments."
              hamperType="VIPOpulence"
            />
          </div>
        </div>
      </section>

      {/* Featured Carousel */}
      <FeaturedCarousel
        products={featuredProducts}
        onClick={handleAddClick}
        onView={handleViewProduct}
        selectedItems={selectedAccessories}
      />

      {/* Flowers Preview */}
      <section className="px-6 py-16 max-w-6xl mx-auto" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-center mb-10">Browse Our Flower Selection</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {flowerProducts.slice(0, 6).map((product, i) => (
            <ProductCard
              key={product._id}
              product={product}
              index={i}
              onClick={() => handleAddClick(product)}
              onView={() => handleViewProduct(product)}
            />
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/flowers">
            <button className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition">
              See All Flowers
            </button>
          </Link>
        </div>
      </section>

      {/* Accessories Preview */}
      <section className="px-6 py-16 max-w-6xl mx-auto" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-center mb-10">Browse Our Accessories</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {accessoriesProducts.slice(0, 6).map((product, i) => (
            <ProductCard
              key={product._id}
              product={product}
              index={i}
              onClick={() => handleAddClick(product)}
              onView={() => handleViewProduct(product)}
            />
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={handleSeeAllAccessories}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
          >
            See All Accessories
          </button>
        </div>
      </section>

      {/* Vips Preview */}
      <section className="px-6 py-16 max-w-6xl mx-auto" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-center mb-10">Browse Our Vips</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {vipProducts.slice(0, 6).map((product, i) => (
            <ProductCard
              key={product._id}
              product={product}
              index={i}
              onClick={() => handleAddClick(product)}
              onView={() => handleViewProduct(product)}
            />
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={handleSeeAllVips}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
          >
            See All Vips
          </button>
        </div>
      </section>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddClick}
        selectedItems={[...selectedAccessories, ...selectedFlowers, ...selectedVips]}
      />

      {/* Footer */}
      <div data-aos="fade-up">
        <Footer />
      </div>
    </>
  );
}

export default Shop;
